import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import { Button, Stack, Typography } from '@mui/material'
import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Red de seguridad de última instancia: sin esto, cualquier excepción no
 * capturada en el árbol de React deja la pantalla en blanco sin explicación.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Error no controlado en la interfaz:', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ minHeight: '100vh', textAlign: 'center', px: 3, bgcolor: 'background.default' }}
      >
        <Typography variant="h4">Algo salió mal</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          Ocurrió un error inesperado en la interfaz. Intenta recargar la página; si el
          problema persiste, contacta al administrador de la plataforma.
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshRoundedIcon />}
          onClick={() => window.location.reload()}
        >
          Recargar
        </Button>
      </Stack>
    )
  }
}
